import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { companyInfo } from '@/mocks/home';
import { getGenericWhatsAppUrl } from '@/utils/whatsapp';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';

const serviceDropdownItems = [
  { label: 'Landscaping', path: '/services/landscaping' },
  { label: 'Interior Works', path: '/services/interior-works' },
  { label: 'UPVC Windows', path: '/services/upvc-windows' },
  { label: 'Flooring & Wall Paneling', path: '/services/flooring-wall-paneling' },
  { label: 'Civil Waterproofing', path: '/services/civil-waterproofing' },
  { label: 'Deep Cleaning', path: '/services/deep-cleaning' },
  { label: 'Swimming Pool & Fountain', path: '/services/swimming-pool-fountain' },
  { label: 'CCTV Surveillance', path: '/services#cctv-surveillance' },
  { label: 'PVC & Bamboo Blinds', path: '/services#pvc-bamboo-blinds' },
  { label: 'Annual Maintenance Contract (AMC)', path: '/services#amc' },
];

const mainLinks = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Services', path: '/services', hasDropdown: true },
  { label: 'Projects', path: '/projects' },
  { label: 'Testimonials', path: '/testimonials' },
  { label: 'Blog', path: '/blog' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const lastScrollY = useRef(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const location = useLocation();

  useBodyScrollLock(mobileOpen);

  const handleScroll = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      const currentY = window.scrollY;
      setScrolled(currentY > 30);
      if (currentY > 80 && currentY > lastScrollY.current + 5) {
        setHidden(true);
      } else if (currentY < lastScrollY.current - 5) {
        setHidden(false);
      }
      lastScrollY.current = currentY;
      rafRef.current = null;
    });
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [handleScroll]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-transform duration-400 will-change-transform ${
        hidden ? '-translate-y-full' : 'translate-y-0'
      } ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2 md:gap-3 shrink-0 cursor-pointer">
            <img
              src={companyInfo.logo}
              alt="Infinite Consultant & Services"
              className="h-10 w-auto md:h-12"
            />
            <span className={`font-heading font-semibold text-sm md:text-base whitespace-nowrap transition-colors duration-300 ${scrolled ? 'text-foreground-950' : 'text-white'}`}>
              Infinite Consultant & Services
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {mainLinks.map((link) => {
              if (link.hasDropdown) {
                const activeServiceRoute = isActive('/services');
                return (
                  <div key={link.path} ref={dropdownRef} className="relative">
                    <button
                      onClick={() => setServicesOpen(!servicesOpen)}
                      className={`relative flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                        activeServiceRoute
                          ? scrolled
                            ? 'text-primary-500'
                            : 'text-white'
                          : scrolled
                            ? 'text-foreground-700 hover:text-primary-500'
                            : 'text-white/90 hover:text-white'
                      }`}
                    >
                      Services
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
                      </div>
                      {activeServiceRoute && (
                        <span className="absolute bottom-0 left-3 right-3 h-[2px] bg-primary-500 rounded-full" style={{ animation: 'underlineExpand 0.3s ease-out forwards', transformOrigin: 'left' }} />
                      )}
                    </button>

                    {servicesOpen && (
                      <div className="absolute top-full mt-1 left-0 w-56 bg-white rounded-xl shadow-lg border border-background-200/70 py-2 animate-fade-in z-50">
                        <Link
                          to="/services"
                          className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50 transition-colors font-medium border-b border-background-100 cursor-pointer"
                        >
                          All Services
                        </Link>
                        {serviceDropdownItems.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            className={`block px-4 py-2.5 text-sm transition-colors cursor-pointer ${
                              location.pathname === item.path
                                ? 'text-primary-500 bg-primary-50 font-medium'
                                : 'text-foreground-700 hover:text-primary-500 hover:bg-primary-50'
                            }`}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              const active = isActive(link.path);
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                    active
                      ? scrolled
                        ? 'text-primary-500'
                        : 'text-white'
                      : scrolled
                        ? 'text-foreground-700 hover:text-primary-500'
                        : 'text-white/90 hover:text-white'
                  }`}
                >
                  {link.label}
                  {active && (
                    <span className="absolute bottom-0 left-3 right-3 h-[2px] bg-primary-500 rounded-full" style={{ animation: 'underlineExpand 0.3s ease-out forwards', transformOrigin: 'left' }} />
                  )}
                </Link>
              );
            })}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={getGenericWhatsAppUrl()}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="btn-ripple px-4 py-2 text-sm font-medium rounded-lg bg-secondary-500 text-white hover:bg-secondary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line mr-1.5" />
              Enquire Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors cursor-pointer ${
              scrolled ? 'text-foreground-950 hover:bg-background-100' : 'text-white hover:bg-white/10'
            }`}
            aria-label="Toggle menu"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`} />
            </div>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200/70 animate-fade-in">
          <div className="px-4 py-4 space-y-1 max-h-[70vh] overflow-y-auto mobile-scroll">
            {mainLinks.map((link) => {
              if (link.hasDropdown) {
                const activeServiceRoute = isActive('/services');
                return (
                  <div key={link.path}>
                    <button
                      onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                      className={`flex items-center justify-between w-full px-4 py-3 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                        activeServiceRoute ? 'text-primary-500 bg-primary-50' : 'text-foreground-700 hover:text-primary-500 hover:bg-primary-50'
                      }`}
                    >
                      Services
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </button>
                    {mobileServicesOpen && (
                      <div className="ml-4 mt-1 space-y-1 border-l-2 border-primary-100 pl-3">
                        <Link
                          to="/services"
                          className="block px-4 py-2.5 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
                        >
                          All Services
                        </Link>
                        {serviceDropdownItems.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            className={`block px-4 py-2.5 text-sm rounded-lg transition-colors cursor-pointer ${
                              location.pathname === item.path
                                ? 'text-primary-500 bg-primary-50 font-medium'
                                : 'text-foreground-700 hover:text-primary-500 hover:bg-primary-50'
                            }`}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-3 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                    isActive(link.path) ? 'text-primary-500 bg-primary-50' : 'text-foreground-700 hover:text-primary-500 hover:bg-primary-50'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
            <div className="pt-3 border-t border-background-200/70 mt-3">
              <a
                href={getGenericWhatsAppUrl()}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-lg bg-secondary-500 text-white cursor-pointer"
              >
                <i className="ri-whatsapp-line" />
                Enquire on WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}